import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { motion } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TrialProvider, isTrialOpenPath } from './contexts/TrialContext';
import TrialWall from './components/trial/TrialWall';
import { initUsageTracker, trackPageview } from './lib/usageTracker';
import { useAdminTz } from './lib/adminTime';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import ExamList from './pages/ExamList';
import ExamDetail from './pages/ExamDetail';
import ExamTake from './pages/ExamTake';
import ExamResults from './pages/ExamResults';
import SkillPractice from './pages/SkillPractice';
import PracticePart from './pages/PracticePart';
import Performance from './pages/Performance';
import Pricing from './pages/Pricing';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import Courses from './pages/Courses';
import CourseViewer from './pages/CourseViewer';
import GroupClasses from './pages/GroupClasses';
import StartHere from './pages/StartHere';
import WhatsIncluded from './pages/WhatsIncluded';
import AuthCallback from './pages/AuthCallback';
import UpgradeModal from './components/UpgradeModal';
import ExitIntentModal from './components/ExitIntentModal';
import Checkout from './pages/Checkout';

// Route-level code splitting. Admin + rarely-visited pages load on demand so the
// student bundle stays small. If a chunk 404s after a deploy (stale index.html),
// reload once to pick up the new hashes instead of showing a blank page.
const lazyRetry = (importer) => lazy(() => importer().catch((err) => {
  const last = Number(sessionStorage.getItem('cmx_chunk_reload') || 0);
  if (Date.now() - last > 60000) {
    sessionStorage.setItem('cmx_chunk_reload', String(Date.now()));
    window.location.reload();
    return new Promise(() => {});
  }
  throw err;
}));
const CrsCalculator = lazyRetry(() => import('./pages/CrsCalculator'));
const TeacherFeedback = lazyRetry(() => import('./pages/TeacherFeedback'));
const LiveRoom = lazyRetry(() => import('./pages/LiveRoom'));
const Settings = lazyRetry(() => import('./pages/Settings'));
const Referral = lazyRetry(() => import('./pages/Referral'));
const Blog = lazyRetry(() => import('./pages/Blog'));
const BlogPost = lazyRetry(() => import('./pages/BlogPost'));
const LegalPage = lazyRetry(() => import('./pages/LegalPage'));
const ForgotPassword = lazyRetry(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyRetry(() => import('./pages/ResetPassword'));
const Contact = lazyRetry(() => import('./pages/Contact'));
const WinningStories = lazyRetry(() => import('./pages/WinningStories'));
const ConnectWhatsApp = lazyRetry(() => import('./pages/ConnectWhatsApp'));
const Notifications = lazyRetry(() => import('./pages/Notifications'));
const MyTickets = lazyRetry(() => import('./pages/MyTickets'));
const AdminCrs = lazyRetry(() => import('./pages/admin/AdminCrs'));
const AdminDashboard = lazyRetry(() => import('./pages/admin/AdminDashboard'));
const AdminDashboardMetrics = lazyRetry(() => import('./pages/admin/AdminDashboardMetrics'));
const AdminQuestions = lazyRetry(() => import('./pages/admin/AdminQuestions'));
const AdminExams = lazyRetry(() => import('./pages/admin/AdminExams'));
const AdminUsers = lazyRetry(() => import('./pages/admin/AdminUsers'));
const AdminReferrals = lazyRetry(() => import('./pages/admin/AdminReferrals'));
const AdminLegal = lazyRetry(() => import('./pages/admin/AdminLegal'));
const AdminHelp = lazyRetry(() => import('./pages/admin/AdminHelp'));
const OnboardingForm = lazyRetry(() => import('./pages/OnboardingForm'));
const AdminBlog = lazyRetry(() => import('./pages/admin/AdminBlog'));
const AdminCourses = lazyRetry(() => import('./pages/admin/AdminCourses'));
const AdminOnboardingForm = lazyRetry(() => import('./pages/admin/AdminOnboardingForm'));
const AdminOnboardingResponses = lazyRetry(() => import('./pages/admin/AdminOnboardingResponses'));
const AdminPricing = lazyRetry(() => import('./pages/admin/AdminPricing'));
const AdminCancellations = lazyRetry(() => import('./pages/admin/AdminCancellations'));
const AdminRevenue = lazyRetry(() => import('./pages/admin/AdminRevenue'));
const AdminAttemptDetail = lazyRetry(() => import('./pages/admin/AdminAttemptDetail'));
const AdminEvents = lazyRetry(() => import('./pages/admin/AdminEvents'));
const AdminStartHere = lazyRetry(() => import('./pages/admin/AdminStartHere'));
const AdminDashboardWidgets = lazyRetry(() => import('./pages/admin/AdminDashboardWidgets'));
const AdminLanding = lazyRetry(() => import('./pages/admin/AdminLanding'));
const AdminContactPage = lazyRetry(() => import('./pages/admin/AdminContactPage'));
const AdminPromotions = lazyRetry(() => import('./pages/admin/AdminPromotions'));
const AdminEmails = lazyRetry(() => import('./pages/admin/AdminEmails'));
const AdminAiPrompts = lazyRetry(() => import('./pages/admin/AdminAiPrompts'));
const AdminAiHealth = lazyRetry(() => import('./pages/admin/AdminAiHealth'));
const AdminWhatsApp = lazyRetry(() => import('./pages/admin/AdminWhatsApp'));
const AdminRefunds = lazyRetry(() => import('./pages/admin/AdminRefunds'));
const AdminCheckout = lazyRetry(() => import('./pages/admin/AdminCheckout'));
const AdminMarketing = lazyRetry(() => import('./pages/admin/AdminMarketing'));
const AdminNotifications = lazyRetry(() => import('./pages/admin/AdminNotifications'));
const AdminSupport = lazyRetry(() => import('./pages/admin/AdminSupport'));
const AdminSales = lazyRetry(() => import('./pages/admin/AdminSales'));
const AdminGrowth = lazyRetry(() => import('./pages/admin/AdminGrowth'));
const AdminStudyPlans = lazyRetry(() => import('./pages/admin/AdminStudyPlans'));
const AdminTeacherFeedback = lazyRetry(() => import('./pages/admin/AdminTeacherFeedback'));
const AdminTrial = lazyRetry(() => import('./pages/admin/AdminTrial'));
const StudyPlan = lazyRetry(() => import('./pages/StudyPlan'));

const RouteSpinner = () => (
  <div className="flex items-center justify-center h-screen" data-testid="route-loading">
    <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

const pageVariants = {
  initial: { opacity: 0, y: 10 },
  enter: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.15, ease: 'easeIn' } },
};

function PageTransition({ children }) {
  return (
    <motion.div variants={pageVariants} initial="initial" animate="enter" exit="exit">
      {children}
    </motion.div>
  );
}

const STAFF_ROLES = ['admin', 'teacher', 'contributor', 'sales'];

function ProtectedRoute({ children, adminOnly = false, roles = null, skipOnboarding = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  // Preserve the intended destination so login can send the user back
  // (e.g. /subscription?session_id=… after a Stripe payment).
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" />;
  // Users who haven't finished onboarding are sent to complete it first
  // (free + paid both land in onboarding → then Start Here). We no longer
  // bounce them to /pricing, which created a confusing loop. Staff roles
  // (admin/teacher/contributor) never get forced into onboarding.
  // Trial mode (Admin → Free vs Trial): unpaid / expired trial students see the lock wall instead of any
  // learning page — they can still reach checkout, settings, tickets and contact.
  if (!STAFF_ROLES.includes(user.role) && user.trial?.locked) {
    // Open paths (checkout, settings, tickets…) render even before onboarding — a locked student can't onboard yet.
    if (isTrialOpenPath(location.pathname)) return children;
    return <TrialWall state={user.trial} />;
  }
  if (!skipOnboarding && !STAFF_ROLES.includes(user.role) && user.onboarding_completed === false) return <Navigate to="/onboarding" />;
  return children;
}

// /admin landing per role: admin sees the metrics dashboard; teachers land on
// Users (student progress), contributors on Exams (content management).
function AdminHome() {
  const { user } = useAuth();
  if (user.role === 'teacher') return <Navigate to="/admin/users" replace />;
  if (user.role === 'contributor') return <Navigate to="/admin/exams" replace />;
  if (user.role === 'sales') return <Navigate to="/admin/sales" replace />;
  return <PageTransition><AdminDashboardMetrics /></PageTransition>;
}

function ReferralCapture() {
  const [refActive, setRefActive] = useState(false);
  const [refName, setRefName] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      localStorage.setItem('ref_code', refCode);
      localStorage.setItem('ref_code_at', Date.now().toString());
    }
    const stored = localStorage.getItem('ref_code');
    if (stored) {
      setRefActive(true);
      setRefName(stored);
    }
  }, []);

  if (!refActive) return null;
  return (
    <div style={{ background: '#059669', color: '#fff', padding: '6px 16px', fontSize: '13px', textAlign: 'center', fontFamily: 'Arial, sans-serif' }} data-testid="referral-banner">
      You were referred by <strong>{refName}</strong> — Sign up to get started!
      <button onClick={() => { localStorage.removeItem('ref_code'); setRefActive(false); }} style={{ marginLeft: '12px', background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>dismiss</button>
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const { user } = useAuth();
  // Admin timezone change remounts the current admin page so every date/chart refetches in the new zone.
  const [adminTz] = useAdminTz();
  const routeKey = location.pathname.startsWith('/admin') ? `${location.pathname}|${adminTz}` : location.pathname;

  // Internal usage tracking — page view on every route change
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);

  return (
    <Suspense fallback={<RouteSpinner />}>
    <Routes location={location} key={routeKey}>
        {/* Public pages */}
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <PageTransition><Landing /></PageTransition>} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <PageTransition><Login /></PageTransition>} />
        <Route path="/verify-email" element={user ? <Navigate to="/dashboard" /> : <PageTransition><VerifyEmail /></PageTransition>} />
        <Route path="/signup" element={<PageTransition><Signup /></PageTransition>} />
        <Route path="/pricing" element={<PageTransition><Pricing /></PageTransition>} />
        <Route path="/terms" element={<PageTransition><LegalPage pageType="terms" fallbackTitle="Terms of Service" /></PageTransition>} />
        <Route path="/privacy" element={<PageTransition><LegalPage pageType="privacy" fallbackTitle="Privacy Policy" /></PageTransition>} />
        <Route path="/refund-policy" element={<PageTransition><LegalPage pageType="refund" fallbackTitle="Refund Policy" /></PageTransition>} />
        <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
        <Route path="/crs-calculator" element={<PageTransition><CrsCalculator /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/winning-stories" element={<PageTransition><WinningStories /></PageTransition>} />
        <Route path="/blog/:slug" element={<PageTransition><BlogPost /></PageTransition>} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<PageTransition><ForgotPassword /></PageTransition>} />
        <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
        {/* Authenticated pages */}
        <Route path="/onboarding" element={<ProtectedRoute skipOnboarding><OnboardingForm /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><PageTransition><Dashboard /></PageTransition></ProtectedRoute>} />
        <Route path="/exams" element={<ProtectedRoute><PageTransition><ExamList /></PageTransition></ProtectedRoute>} />
        <Route path="/exams/:examId" element={<ProtectedRoute><PageTransition><ExamDetail /></PageTransition></ProtectedRoute>} />
        <Route path="/exams/:examId/take" element={<ProtectedRoute><ExamTake /></ProtectedRoute>} />
        <Route path="/exams/:examId/results/:attemptId" element={<ProtectedRoute><PageTransition><ExamResults /></PageTransition></ProtectedRoute>} />
        <Route path="/practice/:skill" element={<ProtectedRoute><PageTransition><SkillPractice /></PageTransition></ProtectedRoute>} />
        <Route path="/practice/:skill/:partId" element={<ProtectedRoute><PracticePart /></ProtectedRoute>} />
        <Route path="/performance" element={<ProtectedRoute><PageTransition><Performance /></PageTransition></ProtectedRoute>} />
        <Route path="/feedback-credits" element={<ProtectedRoute><PageTransition><TeacherFeedback /></PageTransition></ProtectedRoute>} />
        <Route path="/feedback" element={<Navigate to="/feedback-credits" replace />} />
        <Route path="/notifications" element={<ProtectedRoute><PageTransition><Notifications /></PageTransition></ProtectedRoute>} />
        <Route path="/study-plan" element={<ProtectedRoute><PageTransition><StudyPlan /></PageTransition></ProtectedRoute>} />
        <Route path="/my-tickets" element={<ProtectedRoute><PageTransition><MyTickets /></PageTransition></ProtectedRoute>} />
        <Route path="/my-tickets/:ticketId" element={<ProtectedRoute><PageTransition><MyTickets /></PageTransition></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><PageTransition><Settings /></PageTransition></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute skipOnboarding><PageTransition><SubscriptionSuccess /></PageTransition></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute skipOnboarding><Checkout /></ProtectedRoute>} />
        <Route path="/courses" element={<ProtectedRoute><PageTransition><Courses /></PageTransition></ProtectedRoute>} />
        <Route path="/courses/:courseSlug" element={<ProtectedRoute><PageTransition><CourseViewer /></PageTransition></ProtectedRoute>} />
        <Route path="/referral" element={<ProtectedRoute><PageTransition><Referral /></PageTransition></ProtectedRoute>} />
        <Route path="/group-classes" element={<ProtectedRoute><PageTransition><GroupClasses /></PageTransition></ProtectedRoute>} />
        <Route path="/live/:kind/:refId" element={<ProtectedRoute><LiveRoom /></ProtectedRoute>} />
        <Route path="/start-here" element={<ProtectedRoute><PageTransition><StartHere /></PageTransition></ProtectedRoute>} />
        <Route path="/whats-included" element={<ProtectedRoute><PageTransition><WhatsIncluded /></PageTransition></ProtectedRoute>} />
        <Route path="/connect-whatsapp" element={<ProtectedRoute skipOnboarding><PageTransition><ConnectWhatsApp /></PageTransition></ProtectedRoute>} />
        {/* Admin pages */}
        <Route path="/admin" element={<ProtectedRoute roles={STAFF_ROLES}><AdminHome /></ProtectedRoute>} />
        <Route path="/admin/dashboard-old" element={<ProtectedRoute adminOnly><PageTransition><AdminDashboard /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/questions" element={<ProtectedRoute roles={['admin', 'contributor']}><PageTransition><AdminQuestions /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/exams" element={<ProtectedRoute roles={['admin', 'contributor']}><PageTransition><AdminExams /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute roles={['admin', 'teacher', 'sales']}><PageTransition><AdminUsers /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/referrals" element={<ProtectedRoute adminOnly><PageTransition><AdminReferrals /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/blog" element={<ProtectedRoute adminOnly><PageTransition><AdminBlog /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/courses" element={<ProtectedRoute roles={['admin', 'contributor']}><PageTransition><AdminCourses /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/legal" element={<ProtectedRoute adminOnly><PageTransition><AdminLegal /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/onboarding" element={<ProtectedRoute adminOnly><PageTransition><AdminOnboardingForm /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/onboarding/responses" element={<ProtectedRoute roles={['admin', 'teacher']}><PageTransition><AdminOnboardingResponses /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/pricing" element={<ProtectedRoute adminOnly><PageTransition><AdminPricing /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/cancellations" element={<ProtectedRoute adminOnly><PageTransition><AdminCancellations /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/help" element={<ProtectedRoute adminOnly><PageTransition><AdminHelp /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/revenue" element={<ProtectedRoute adminOnly><PageTransition><AdminRevenue /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/attempts/:id" element={<ProtectedRoute roles={['admin', 'teacher', 'sales']}><PageTransition><AdminAttemptDetail /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/events" element={<ProtectedRoute roles={['admin', 'teacher']}><PageTransition><AdminEvents /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/start-here" element={<ProtectedRoute adminOnly><PageTransition><AdminStartHere /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/student-dashboard" element={<ProtectedRoute adminOnly><PageTransition><AdminDashboardWidgets /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/landing" element={<ProtectedRoute adminOnly><PageTransition><AdminLanding /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/contact-page" element={<ProtectedRoute adminOnly><PageTransition><AdminContactPage /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/promotions" element={<ProtectedRoute adminOnly><PageTransition><AdminPromotions /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/checkout" element={<ProtectedRoute adminOnly><PageTransition><AdminCheckout /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/marketing" element={<ProtectedRoute adminOnly><PageTransition><AdminMarketing /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/emails" element={<ProtectedRoute adminOnly><PageTransition><AdminEmails /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/notifications" element={<ProtectedRoute adminOnly><PageTransition><AdminNotifications /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/support" element={<ProtectedRoute adminOnly><PageTransition><AdminSupport /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/sales" element={<ProtectedRoute roles={['admin', 'sales']}><PageTransition><AdminSales /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/growth" element={<ProtectedRoute adminOnly><PageTransition><AdminGrowth /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/study-plans" element={<ProtectedRoute roles={['admin', 'teacher']}><PageTransition><AdminStudyPlans /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/teacher" element={<ProtectedRoute roles={['admin', 'teacher']}><PageTransition><AdminTeacherFeedback /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/ai-prompts" element={<ProtectedRoute adminOnly><PageTransition><AdminAiPrompts /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/ai-health" element={<ProtectedRoute adminOnly><PageTransition><AdminAiHealth /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/crs" element={<ProtectedRoute adminOnly><PageTransition><AdminCrs /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/whatsapp" element={<ProtectedRoute adminOnly><PageTransition><AdminWhatsApp /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/refunds" element={<ProtectedRoute adminOnly><PageTransition><AdminRefunds /></PageTransition></ProtectedRoute>} />
        <Route path="/admin/trial" element={<ProtectedRoute adminOnly><PageTransition><AdminTrial /></PageTransition></ProtectedRoute>} />
        {/* Legacy/orphan route redirects (iter55 button audit cleanup) */}
        <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
        <Route path="/onboarding-summary" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  useEffect(() => { initUsageTracker(); }, []);
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <TrialProvider>
            <ReferralCapture />
            <AnimatedRoutes />
            <UpgradeModal />
            <ExitIntentModal />
            <Toaster position="top-right" richColors closeButton />
          </TrialProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

